import router from '@/router';
import Axios, {AxiosError} from 'axios';
import store from '../../store/store';
import { SET_USER_DETAILS } from '@/store/MutationTypes';
import UserDetails from '@/models/User/UserDetails';
import { UserTypes } from '@/models/User/UserTypes';
import { resource as nutritionLabelRepositoryResource } from "@/utils/API/NutritionLabelRepository";
import {CustomerCreationMode} from "@/models/Customer/CustomerCreationMode";

const baseURL = process.env.VUE_APP_BASE_DOMAIN + process.env.VUE_APP_BASE_DIRECTORY;

const repository = Axios.create({
  baseURL,
  // headers: {'Authorization' : ''}
});

const pendingRequests = new Map();

// TODO: Figure out a dynamic way of implementing route based rules
// https://medium.com/@velja/cancelling-duplicate-requests-with-axios-076813d3a343

const URL_CANCELLATION_ALLOW_LIST: string[] = [
  `${nutritionLabelRepositoryResource}/previewNutritionLabelForRecipe`,
  `${nutritionLabelRepositoryResource}/getFreeTextPlaceholdersForNutritionLabel`,
  `${nutritionLabelRepositoryResource}/Download`,
];

repository.interceptors.request.use(
  (config) => {
    // We only want to apply cancellation on a whitelist basis
    if (!URL_CANCELLATION_ALLOW_LIST.includes(config.url)) {
      return config;
    }

    const identifier = `${config.url}_${config.method}`;

    if (pendingRequests.has(identifier)) {
      const cancellationTokenSource = pendingRequests.get(identifier);
      cancellationTokenSource.cancel('New request spawned.');
      console.log(`Cancelling request with identifier: ${identifier}`);
    }

    // otherwise create a cancellation token
    const cancellationTokenSource = Axios.CancelToken.source();
    config.cancelToken = cancellationTokenSource.token;

    pendingRequests.set(identifier, cancellationTokenSource);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

repository.interceptors.response.use(
  (response) => {
    const requestIdentifier = `${response.config.url}_${response.config.method}`;
    pendingRequests.delete(requestIdentifier);
    return response;
  },
  async(err: AxiosError) => {
    if (err.response) {
      // If a request set the X-Suppress-Auth-Redirect header we should not
      // automatically force a logout / redirect on 401. This is used by
      // callers that are attempting to refresh or validate the session and
      // want to handle failures themselves.
      const suppressRedirect = err.config && err.config.headers && (err.config.headers['X-Suppress-Auth-Redirect'] || err.config.headers['x-suppress-auth-redirect']);

      if (err.response.status === 401 && !suppressRedirect) {
        store.commit(SET_USER_DETAILS, {
          userDetails: new UserDetails(UserTypes.Guest, '', ''),
          logged: false,
        });
        await router.push('/login');
      }

      // check if subscription or trial is expired
      if (err.response.status === 402) {
        await handleSubscriptionExpired();
      }

      // This is being commented out as it is causing issues with the login page and we are unable to catch the 403 code if the user is not activated.
      // if (err.response.status === 403) {
      //   await router.push('/');
      // }

      // remove failed request from the pending map
      if (err.config) {
        const requestIdentifier = `${err.config.url}_${err.config.method}`;
        pendingRequests.delete(requestIdentifier);
      }

      return Promise.reject(err);
    }
  });

/**
 * Handles the subscription expiration logic by directing the user to the subscription page.
 * It checks if the user's trial has expired and updates the query parameters accordingly.
 * @return {Promise<void>} A promise that resolves once the navigation to the subscription page is completed.
 */
async function handleSubscriptionExpired(): Promise<void> {
  let query;
  const returnUrl = router.currentRoute.fullPath;

  if (store.state.AccountModule.userDetails.creationMode === CustomerCreationMode.Direct) {
    query = { directSignup: 'true', returnUrl };
  }
  else {
    query = {
      trialExpired: store.state.AccountModule.userDetails.isTrial.toString(),
      returnUrl
    };
  }

  await router.push({ path: '/subscription', query });
}

export default repository;